"use client";

import {
  BookOpen,
  ChevronDown,
  ListChecks,
  Paperclip,
  Scale,
} from "lucide-react";
import { useState } from "react";

import { materialIcon, materialsMeta } from "@/lib/assessments/materials";
import {
  type PublishedEntryView,
  publishedListSub,
  publishedListTitle,
} from "@/lib/assessments/published-list";
import { cn } from "@/lib/utils";

/**
 * One material an exam is sat against.
 *
 * `url` absent is the ordinary case on a public page: the file is handed over
 * when an attempt starts, and the row says so rather than linking to it.
 */
export interface BriefMaterial {
  name: string;
  url?: string | null;
}

export interface BriefSectionsProps {
  brief: string;
  materials: BriefMaterial[];
  prelist: PublishedEntryView[];
  /** From `scoringRules`, so the two surfaces cannot state different rules. */
  rules: [string, string][];
  showExpandAll?: boolean;
}

/**
 * The packet as a set of collapsible sections, shared by every surface that
 * shows it: the board page for somebody deciding whether to sit the exam, the
 * brief for somebody about to, and the form they write into.
 *
 * The brief leads, because what the artefact is has to land before the files
 * themselves mean anything. Every section starts closed: the packet reads as a
 * table of contents somebody chooses their way into, and the published list is
 * rows nobody should have to scroll past to reach the ground rules.
 */
export function BriefSections({
  brief,
  materials,
  prelist,
  rules,
  showExpandAll = true,
}: BriefSectionsProps) {
  const sections = [
    {
      id: "brief",
      icon: BookOpen,
      title: "What you are looking at",
      sub: "The artefact, and what to do with it",
      meta: null,
      body: <p className="leading-relaxed whitespace-pre-wrap">{brief}</p>,
    },
    // An exam with no materials has no row for them, rather than a section that
    // opens onto nothing.
    ...(materials.length > 0
      ? [
          {
            id: "materials",
            icon: Paperclip,
            title: "Materials",
            sub: "Open these while you write",
            meta: materialsMeta(materials.map((file) => file.name)),
            body: <MaterialList files={materials} />,
          },
        ]
      : []),
    {
      id: "scored",
      icon: Scale,
      title: "How this is scored",
      sub: "Applied before a person reads your work",
      meta: `${String(rules.length)} rules`,
      body: <ItemList rows={rules} />,
    },
    {
      id: "risks",
      icon: ListChecks,
      title: publishedListTitle(prelist),
      sub: publishedListSub(prelist),
      meta: `${String(prelist.length)} items`,
      body: <PublishedList entries={prelist} />,
    },
  ];

  const [open, setOpen] = useState<string[]>([]);
  const allOpen = sections.every((section) => open.includes(section.id));

  const toggle = (id: string) => {
    setOpen((prev) =>
      prev.includes(id) ? prev.filter((each) => each !== id) : [...prev, id],
    );
  };

  return (
    <div>
      {showExpandAll ? (
        <div className="flex justify-end">
          <button
            className="text-foreground/70 hover:text-marketing-blue cursor-pointer text-sm transition-colors"
            onClick={() => {
              setOpen(allOpen ? [] : sections.map((section) => section.id));
            }}
            type="button"
          >
            {allOpen ? "Collapse all" : "Expand all"}
          </button>
        </div>
      ) : null}

      <div className="border-border mt-3 border-t">
        {sections.map((section) => {
          const isOpen = open.includes(section.id);
          return (
            <div className="border-border border-b" key={section.id}>
              <button
                aria-expanded={isOpen}
                className="group flex w-full cursor-pointer items-center gap-3.5 px-1 py-5 text-left"
                onClick={() => {
                  toggle(section.id);
                }}
                type="button"
              >
                <section.icon className="text-foreground/50 size-[18px] flex-none" />
                <span className="min-w-0 flex-1">
                  <span className="group-hover:text-marketing-blue block font-semibold tracking-[-0.01em] transition-colors">
                    {section.title}
                  </span>
                  <span className="text-foreground/70 mt-0.5 block text-sm">
                    {section.sub}
                  </span>
                </span>
                {section.meta ? (
                  <span className="text-foreground/50 flex-none text-sm whitespace-nowrap">
                    {section.meta}
                  </span>
                ) : null}
                <ChevronDown
                  className={cn(
                    "text-foreground/50 size-4 flex-none transition-transform duration-200 ease-out",
                    isOpen && "rotate-180",
                  )}
                />
              </button>
              {/* pl-9 clears the icon, so the body lines up under the section
                  title rather than starting left of it. */}
              {isOpen ? (
                <div className="pr-1 pb-7 pl-9">{section.body}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The published list, in whichever shape the exam holds.
 *
 * An example finding renders as the object it is: its location, its title, and
 * its fields under the labels the submission form uses. That is the half of
 * ADR-0014 the reader sees -- somebody deciding what to write is shown the exact
 * form they have to produce, rather than a category they have to translate.
 *
 * A legacy risk has one field and no location, so it falls through to the same
 * rows with nothing above them, which is all there ever was to show.
 */
function PublishedList({ entries }: { entries: PublishedEntryView[] }) {
  return (
    <dl className="divide-border divide-y">
      {entries.map((entry) => (
        <div className="py-5 first:pt-1 last:pb-1" key={entry.ref}>
          <dt className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <span className="text-foreground/50 font-mono text-xs tracking-tight tabular-nums">
              {entry.ref}
            </span>
            <span className="text-[15px] font-medium">{entry.heading}</span>
            {entry.location ? (
              <span className="border-border text-foreground/60 rounded-md border px-1.5 py-0.5 text-xs">
                {entry.location}
              </span>
            ) : null}
          </dt>
          <dd className="mt-2.5 space-y-2">
            {entry.fields.map((field) => (
              <p className="text-[15px] leading-relaxed" key={field.label}>
                <span className="text-foreground/50">{field.label}. </span>
                <span className="text-foreground/70">{field.value}</span>
              </p>
            ))}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function MaterialList({ files }: { files: BriefMaterial[] }) {
  return (
    <div className="-mx-2 flex flex-col">
      {files.map((file) => {
        const Icon = materialIcon(file.name);
        return (
          <div
            className="hover:bg-muted flex items-center gap-3 rounded-lg px-2 py-2 transition-colors"
            key={file.name}
          >
            <Icon className="text-foreground/40 size-4 flex-none" />
            <span
              className="min-w-0 flex-1 truncate text-[15px] font-medium"
              title={file.name}
            >
              {file.name}
            </span>
            {file.url ? (
              <a
                className="text-marketing-blue hover:bg-marketing-blue/10 inline-flex flex-none items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium transition-colors"
                href={file.url}
                rel="noreferrer"
                target="_blank"
              >
                Open
              </a>
            ) : (
              <span className="text-foreground/50 flex-none text-sm">
                Opens with the task
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A bulleted list, not cards and not a table.
 *
 * The marker gives each entry a place for the eye to land, which is what both
 * a divider per row and no divider at all failed to do, and it costs one glyph
 * rather than an edge around twelve boxes.
 */
function ItemList({ rows }: { rows: [string, string][] }) {
  return (
    // `divide-y` rather than a border on each row: it draws between children
    // only, so the list adds no line of its own at either end and cannot double
    // up against the section divider the way a border-per-row did.
    <dl className="divide-border divide-y">
      {rows.map(([term, detail]) => (
        <div className="py-5 pl-5 first:pt-1 last:pb-1" key={term}>
          {/* The marker hangs off the title as a pseudo-element rather than a
              sibling span, so `dt` and `dd` stay direct children of the row. */}
          <dt className="before:bg-foreground/25 relative text-[15px] font-medium before:absolute before:top-2 before:-left-5 before:size-1.5 before:rounded-full before:content-['']">
            {term}
          </dt>
          <dd className="text-foreground/70 mt-2 text-[15px] leading-relaxed">
            {detail}
          </dd>
        </div>
      ))}
    </dl>
  );
}
